import {
  AssetIncentiveAllocation,
  ActionOnAllocationAccountsResponse,
  Clientize,
  CreateAllocationRequest,
  CreateIncentiveRequest,
  DataFilterDisplay,
  GetAccountsForAllocationResponse,
  Incentive,
  IncentiveSearchParams,
  IncentiveSearchResponse,
  SearchIncentiveAccountsResponse,
  UpdateAllocationRequest,
  UpdateIncentiveRequest,
  RECIncentiveType,
  AssignAccountsRequest,
} from 'types';

import { api } from './api';

interface IncentiveAccountsSearchParams {
  key: string;
  searchTerm?: string;
  pageNum?: number;
  pageSize?: number;
  advancedFilters?: string;
}

export const incentivesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getIncentives: build.query<IncentiveSearchResponse, IncentiveSearchParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params.searchTerm) searchParams.set('searchTerm', params.searchTerm);
        if (params.pageNum) searchParams.set('pageNum', params.pageNum.toString());
        if (params.pageSize) searchParams.set('pageSize', params.pageSize.toString());
        if (params.advancedFilters) searchParams.set('advancedFilters', params.advancedFilters);

        const queryString = searchParams.toString();
        return {
          url: `/admin/incentives${queryString ? `?${queryString}` : ''}`,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.incentives.map(({ key }) => ({ type: 'Incentives' as const, id: key })),
              { type: 'Incentives', id: 'LIST' },
            ]
          : [{ type: 'Incentives', id: 'LIST' }],
    }),

    getIncentiveFilters: build.query<DataFilterDisplay[], void>({
      query: () => ({ url: '/admin/incentives/filters' }),
    }),

    getIncentive: build.query<Clientize<Incentive>, { key: string }>({
      query: ({ key }) => ({ url: `/admin/incentives/${key}` }),
      providesTags: (_result, _error, { key }) => [{ type: 'Incentives', id: key }],
    }),

    createIncentive: build.mutation<Incentive, CreateIncentiveRequest>({
      query: (body) => ({
        url: '/admin/incentives',
        method: 'POST',
        body,
      }),
      invalidatesTags: () => [{ type: 'Incentives', id: 'LIST' }],
    }),

    updateIncentive: build.mutation<Incentive, { key: string; data: UpdateIncentiveRequest }>({
      query: ({ key, data }) => ({
        url: `/admin/incentives/${key}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { key }) => [{ type: 'Incentives', id: key }],
    }),

    createAllocation: build.mutation<
      ActionOnAllocationAccountsResponse,
      { key: string; data: CreateAllocationRequest & AssignAccountsRequest }
    >({
      query: ({ key, data }) => ({
        url: `/admin/incentives/${key}/allocations`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { key }) => [{ type: 'Incentives', id: key }],
    }),

    updateAllocation: build.mutation<
      Clientize<AssetIncentiveAllocation>,
      { key: string; allocationId: string; data: UpdateAllocationRequest }
    >({
      query: ({ key, allocationId, data }) => ({
        url: `/admin/incentives/${key}/allocations/${allocationId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { key }) => [{ type: 'Incentives', id: key }],
    }),

    assignAccountsToAllocation: build.mutation<
      ActionOnAllocationAccountsResponse,
      { key: string; allocationId: string; accountIds: string[]; recType: RECIncentiveType }
    >({
      query: ({ key, allocationId, accountIds, recType }) => ({
        url: `/admin/incentives/${key}/allocations/${allocationId}/assign`,
        method: 'POST',
        body: { accountIds, recType },
      }),
      invalidatesTags: (_result, _error, { key }) => [{ type: 'Incentives', id: key }],
    }),
    unallocateAccountsFromAllocation: build.mutation<
      ActionOnAllocationAccountsResponse,
      { key: string; allocationId: string; accountIds: string[] }
    >({
      query: ({ key, allocationId, accountIds }) => ({
        url: `/admin/incentives/${key}/allocations/${allocationId}/unallocate`,
        method: 'POST',
        body: { accountIds },
      }),
      invalidatesTags: (_result, _error, { key }) => [{ type: 'Incentives', id: key }],
    }),

    blockAccountsFromAllocation: build.mutation<
      ActionOnAllocationAccountsResponse,
      { key: string; allocationId: string; accountIds: string[]; blockReason: string }
    >({
      query: ({ key, allocationId, accountIds, blockReason }) => ({
        url: `/admin/incentives/${key}/allocations/${allocationId}/block`,
        method: 'POST',
        body: { accountIds, blockReason },
      }),
      invalidatesTags: (_result, _error, { key }) => [{ type: 'Incentives', id: key }],
    }),

    getAllocationAccounts: build.query<
      Clientize<GetAccountsForAllocationResponse>,
      { key: string; allocationId: string }
    >({
      query: ({ key, allocationId }) => ({
        url: `/admin/incentives/${key}/allocations/${allocationId}/accounts`,
      }),
      providesTags: (_result, _error, { key }) => [{ type: 'Incentives', id: key }],
    }),

    searchIncentiveAccounts: build.query<Clientize<SearchIncentiveAccountsResponse>, IncentiveAccountsSearchParams>({
      query: ({ key, searchTerm, pageNum, pageSize, advancedFilters }) => {
        const searchParams = new URLSearchParams();
        if (searchTerm) searchParams.set('searchTerm', searchTerm);
        if (pageNum) searchParams.set('pageNum', pageNum.toString());
        if (pageSize) searchParams.set('pageSize', pageSize.toString());
        if (advancedFilters) searchParams.set('advancedFilters', advancedFilters);

        const queryString = searchParams.toString();
        return {
          url: `/admin/incentives/${key}/accounts/search${queryString ? `?${queryString}` : ''}`,
        };
      },
      providesTags: (_result, _error, { key }) => [{ type: 'Incentives', id: key }],
    }),

    getIncentiveAccountFilters: build.query<DataFilterDisplay[], { key: string }>({
      query: ({ key }) => ({ url: `/admin/incentives/${key}/accounts/filters` }),
      providesTags: (_result, _error, { key }) => [{ type: 'Incentives', id: key }],
    }),
  }),
});

export const {
  useGetIncentivesQuery,
  useLazyGetIncentivesQuery,
  useGetIncentiveFiltersQuery,
  useGetIncentiveQuery,
  useLazyGetIncentiveQuery,
  useCreateIncentiveMutation,
  useUpdateIncentiveMutation,
  useCreateAllocationMutation,
  useUpdateAllocationMutation,
  useAssignAccountsToAllocationMutation,
  useBlockAccountsFromAllocationMutation,
  useUnallocateAccountsFromAllocationMutation,
  useGetAllocationAccountsQuery,
  useLazyGetAllocationAccountsQuery,
  useSearchIncentiveAccountsQuery,
  useLazySearchIncentiveAccountsQuery,
  useGetIncentiveAccountFiltersQuery,
} = incentivesApi;
